import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ThemeToggle from '../ThemeToggle';
import { t } from '../../utils/translations';
import ruleeLogoPrimary from '../../assets/rulee-logo-primary.png';
import './LandingPage.css';

const LandingPage = () => {
  const navigate = useNavigate();
  const howItWorksRef = useRef(null);
  const quizGenerationTimerRef = useRef(null);
  const [language, setLanguage] = useState('en');
  const [demoTab, setDemoTab] = useState('subjects');
  const [demoSubject, setDemoSubject] = useState('math');
  const [demoQuizSource, setDemoQuizSource] = useState('auto');
  const [demoQuestionCount, setDemoQuestionCount] = useState(6);
  const [demoGenerating, setDemoGenerating] = useState(false);
  const [demoQuizReady, setDemoQuizReady] = useState(false);

  useEffect(() => {
    const savedLanguage = localStorage.getItem('userLanguage') || 'en';
    setLanguage(savedLanguage);
  }, []);

  useEffect(() => {
    return () => {
      if (quizGenerationTimerRef.current) {
        clearTimeout(quizGenerationTimerRef.current);
      }
    };
  }, []);

  const problemPoints = useMemo(
    () => [
      t('landing.problem.point1', language),
      t('landing.problem.point2', language),
      t('landing.problem.point3', language)
    ],
    [language]
  );

  const differenceRows = useMemo(
    () => [
      [t('landing.difference.monitoringCol', language), t('landing.difference.ruleeCol', language)],
      [t('landing.difference.row1Left', language), t('landing.difference.row1Right', language)],
      [t('landing.difference.row2Left', language), t('landing.difference.row2Right', language)],
      [t('landing.difference.row3Left', language), t('landing.difference.row3Right', language)],
      [t('landing.difference.row4Left', language), t('landing.difference.row4Right', language)]
    ],
    [language]
  );

  const privacyPoints = useMemo(
    () => [
      t('landing.privacy.point1', language),
      t('landing.privacy.point2', language),
      t('landing.privacy.point3', language),
      t('landing.privacy.point4', language)
    ],
    [language]
  );

  const trustPoints = useMemo(
    () => [
      t('landing.trust.point1', language),
      t('landing.trust.point2', language),
      t('landing.trust.point3', language),
      t('landing.trust.point4', language)
    ],
    [language]
  );

  const kidsFeatureCards = useMemo(
    () => [
      {
        title: t('landing.kidsDemo.featureChatTitle', language),
        body: t('landing.kidsDemo.featureChatBody', language)
      },
      {
        title: t('landing.kidsDemo.featureConversationsTitle', language),
        body: t('landing.kidsDemo.featureConversationsBody', language)
      },
      {
        title: t('landing.kidsDemo.featureSubjectsTitle', language),
        body: t('landing.kidsDemo.featureSubjectsBody', language)
      },
      {
        title: t('landing.kidsDemo.featureQuizTitle', language),
        body: t('landing.kidsDemo.featureQuizBody', language)
      },
      {
        title: t('landing.kidsDemo.featureAddSectionTitle', language),
        body: t('landing.kidsDemo.featureAddSectionBody', language)
      },
      {
        title: t('landing.kidsDemo.featureLearnSectionTitle', language),
        body: t('landing.kidsDemo.featureLearnSectionBody', language)
      },
      {
        title: t('landing.kidsDemo.featureQuizHistoryTitle', language),
        body: t('landing.kidsDemo.featureQuizHistoryBody', language)
      }
    ],
    [language]
  );

  const kidsFlowSteps = useMemo(
    () => [
      {
        title: t('landing.kidsDemo.flow1Title', language),
        body: t('landing.kidsDemo.flow1Body', language)
      },
      {
        title: t('landing.kidsDemo.flow2Title', language),
        body: t('landing.kidsDemo.flow2Body', language)
      },
      {
        title: t('landing.kidsDemo.flow3Title', language),
        body: t('landing.kidsDemo.flow3Body', language)
      },
      {
        title: t('landing.kidsDemo.flow4Title', language),
        body: t('landing.kidsDemo.flow4Body', language)
      }
    ],
    [language]
  );

  const demoSubjects = useMemo(
    () => [
      { key: 'math', name: 'Mathematics', emoji: '🧮', topic: 'Fractions and ratios' },
      { key: 'science', name: 'Science', emoji: '🔬', topic: 'Solar system basics' },
      { key: 'english', name: 'English', emoji: '📚', topic: 'Reading comprehension' },
      { key: 'history', name: 'History', emoji: '🏛️', topic: 'Ancient civilizations' }
    ],
    []
  );

  const demoConversations = useMemo(
    () => [
      { title: 'Fractions practice chat', type: 'Section Learning', date: 'Today' },
      { title: 'Science Q&A', type: 'General Chat', date: 'Yesterday' },
      { title: 'Quiz review: Mathematics', type: 'Quiz', date: '2 days ago' }
    ],
    []
  );

  const demoQuizQuestions = useMemo(
    () => [
      {
        question: 'If 3/4 of a class are present and there are 28 students, how many are present?',
        options: ['20', '21', '24', '18']
      },
      {
        question: 'Which fraction is equivalent to 1/2?',
        options: ['2/3', '3/6', '4/10', '5/12']
      }
    ],
    []
  );

  const selectedDemoSubject = useMemo(
    () => demoSubjects.find((subject) => subject.key === demoSubject) || demoSubjects[0],
    [demoSubject, demoSubjects]
  );

  const onStartFamily = () => {
    navigate('/login');
  };

  const onSeeHowItWorks = () => {
    howItWorksRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const onDemoGenerateQuiz = () => {
    setDemoTab('quiz');
    setDemoGenerating(true);
    setDemoQuizReady(false);
    if (quizGenerationTimerRef.current) {
      clearTimeout(quizGenerationTimerRef.current);
    }
    quizGenerationTimerRef.current = setTimeout(() => {
      setDemoGenerating(false);
      setDemoQuizReady(true);
    }, 1600);
  };

  return (
    <div className="landing-page">
      <header className="landing-header">
        <div className="landing-brand" aria-label="Rulee AI logo">
          <img src={ruleeLogoPrimary} alt="Rulee AI" className="landing-brand-logo" />
        </div>
        <div className="landing-header-actions">
          <button type="button" className="landing-header-login" onClick={() => navigate('/login')}>
            {t('login', language)}
          </button>
          <ThemeToggle />
        </div>
      </header>

      <main>
        <section className="landing-section landing-hero">
          <div className="landing-hero-copy">
            <p className="landing-eyebrow">{t('landing.hero.eyebrow', language)}</p>
            <h1>{t('landing.hero.headline', language)}</h1>
            <p className="landing-subheadline">{t('landing.hero.subheadline', language)}</p>
            <div className="landing-hero-actions">
              <button className="btn landing-primary-cta" onClick={onStartFamily}>
                {t('landing.hero.primaryCta', language)}
              </button>
              <button className="landing-secondary-cta" onClick={onSeeHowItWorks}>
                {t('landing.hero.secondaryCta', language)}
              </button>
            </div>
            <p className="landing-microcopy">{t('landing.hero.earlyAccess', language)}</p>
            <div className="landing-badges">
              <span>{t('landing.badges.privacyFirst', language)}</span>
              <span>{t('landing.badges.noAds', language)}</span>
              <span>{t('landing.badges.noDataSelling', language)}</span>
            </div>
          </div>

          <div className="landing-hero-visual" aria-label="Parent and child using Rulee together">
            <div className="landing-visual-caption">{t('landing.hero.visualCaption', language)}</div>
            <div className="landing-device">
              <div className="landing-device-screen">
                <p className="landing-card-label">{t('landing.hero.insightLabel', language)}</p>
                <p>{t('landing.hero.insightText', language)}</p>
              </div>
            </div>
          </div>
        </section>

        <section className="landing-section landing-problem landing-section-soft">
          <img src={ruleeLogoPrimary} alt="Rulee AI" className="landing-inline-logo" />
          <p className="landing-section-kicker">{t('landing.problem.kicker', language)}</p>
          <h2>{t('landing.problem.headline', language)}</h2>
          <ul>
            {problemPoints.map((point) => (
              <li key={point}>{point}</li>
            ))}
          </ul>
          <p className="landing-transition">{t('landing.problem.transition', language)}</p>
        </section>

        <section id="how-it-works" ref={howItWorksRef} className="landing-section landing-how">
          <img src={ruleeLogoPrimary} alt="Rulee AI" className="landing-inline-logo" />
          <p className="landing-section-kicker">{t('landing.kidsDemo.kicker', language)}</p>
          <h2>{t('landing.kidsDemo.headline', language)}</h2>
          <p className="landing-subheadline">{t('landing.kidsDemo.subheadline', language)}</p>

          <h3 className="landing-kidsdemo-subtitle">{t('landing.kidsDemo.featureTitle', language)}</h3>
          <div className="landing-kidsdemo-feature-grid">
            {kidsFeatureCards.map((card) => (
              <article className="landing-card" key={card.title}>
                <h4>{card.title}</h4>
                <p>{card.body}</p>
              </article>
            ))}
          </div>

          <h3 className="landing-kidsdemo-subtitle">{t('landing.kidsDemo.flowTitle', language)}</h3>
          <div className="landing-kidsdemo-flow-grid">
            {kidsFlowSteps.map((step, index) => (
              <article className="landing-card" key={step.title}>
                <p className="landing-step-label">Step {index + 1}</p>
                <h4>{step.title}</h4>
                <p>{step.body}</p>
              </article>
            ))}
          </div>

          <h3 className="landing-kidsdemo-subtitle">{t('landing.kidsDemo.demoTitle', language)}</h3>
          <div className="landing-kidsdemo-shell">
            <div className="landing-kidsdemo-tabs">
              <button
                type="button"
                className={`landing-kidsdemo-tab ${demoTab === 'subjects' ? 'active' : ''}`}
                onClick={() => setDemoTab('subjects')}
              >
                {t('landing.kidsDemo.demoTabSubjects', language)}
              </button>
              <button
                type="button"
                className={`landing-kidsdemo-tab ${demoTab === 'conversations' ? 'active' : ''}`}
                onClick={() => setDemoTab('conversations')}
              >
                {t('landing.kidsDemo.demoTabConversations', language)}
              </button>
              <button
                type="button"
                className={`landing-kidsdemo-tab ${demoTab === 'quiz' ? 'active' : ''}`}
                onClick={() => setDemoTab('quiz')}
              >
                {t('landing.kidsDemo.demoTabQuiz', language)}
              </button>
            </div>

            <div className="landing-kidsdemo-panel">
              {demoTab === 'subjects' && (
                <div>
                  <h4>{t('landing.kidsDemo.demoSubjectsTitle', language)}</h4>
                  <p className="landing-kidsdemo-hint">{t('landing.kidsDemo.demoSubjectsHint', language)}</p>
                  <div className="landing-kidsdemo-subject-grid">
                    {demoSubjects.map((subject) => (
                      <button
                        type="button"
                        key={subject.key}
                        className={`landing-kidsdemo-subject-card ${demoSubject === subject.key ? 'active' : ''}`}
                        onClick={() => setDemoSubject(subject.key)}
                      >
                        <span className="landing-kidsdemo-emoji">{subject.emoji}</span>
                        <span>{subject.name}</span>
                      </button>
                    ))}
                  </div>
                  <div className="landing-kidsdemo-controls">
                    <label>
                      {t('landing.kidsDemo.demoSourceLabel', language)}
                      <select value={demoQuizSource} onChange={(event) => setDemoQuizSource(event.target.value)}>
                        <option value="auto">{t('landing.kidsDemo.demoSourceAuto', language)}</option>
                        <option value="weak">{t('landing.kidsDemo.demoSourceWeak', language)}</option>
                      </select>
                    </label>
                    <label>
                      {t('landing.kidsDemo.demoQuestionsLabel', language)}
                      <select value={demoQuestionCount} onChange={(event) => setDemoQuestionCount(Number(event.target.value))}>
                        <option value={5}>5</option>
                        <option value={6}>6</option>
                        <option value={8}>8</option>
                        <option value={10}>10</option>
                      </select>
                    </label>
                  </div>
                  <div className="landing-kidsdemo-actions">
                    <button type="button" className="btn-secondary">
                      {t('landing.kidsDemo.demoAddSection', language)}
                    </button>
                    <button type="button" className="btn-secondary">
                      {t('landing.kidsDemo.demoLearnSection', language)}
                    </button>
                    <button type="button" className="btn landing-kidsdemo-generate-btn" onClick={onDemoGenerateQuiz}>
                      {t('landing.kidsDemo.demoGenerateQuiz', language)}
                    </button>
                  </div>
                </div>
              )}

              {demoTab === 'conversations' && (
                <div>
                  <h4>{t('landing.kidsDemo.demoConversationsTitle', language)}</h4>
                  <p className="landing-kidsdemo-hint">{t('landing.kidsDemo.demoConversationsHint', language)}</p>
                  <div className="landing-kidsdemo-list">
                    {demoConversations.map((conversation) => (
                      <article key={conversation.title} className="landing-kidsdemo-list-item">
                        <div>
                          <strong>{conversation.title}</strong>
                          <p>{conversation.type}</p>
                        </div>
                        <span>{conversation.date}</span>
                      </article>
                    ))}
                  </div>
                </div>
              )}

              {demoTab === 'quiz' && (
                <div>
                  <h4>{t('landing.kidsDemo.demoQuizTitle', language)}</h4>
                  <p className="landing-kidsdemo-hint">{t('landing.kidsDemo.demoQuizHint', language)}</p>
                  <div className="landing-kidsdemo-quiz-meta">
                    <span>{t('landing.kidsDemo.demoSourceLabel', language)}: {demoQuizSource === 'auto' ? t('landing.kidsDemo.demoSourceAuto', language) : t('landing.kidsDemo.demoSourceWeak', language)}</span>
                    <span>{t('landing.kidsDemo.demoQuestionsLabel', language)}: {demoQuestionCount}</span>
                    <span>{t('landing.kidsDemo.demoSubjectLabel', language)}: {selectedDemoSubject.name}</span>
                  </div>
                  {demoGenerating ? (
                    <div className="landing-kidsdemo-generating">
                      <p>{t('landing.kidsDemo.demoGenerating', language)}</p>
                      <div className="landing-kidsdemo-loading-bar">
                        <div className="landing-kidsdemo-loading-progress" />
                      </div>
                    </div>
                  ) : (
                    <div className="landing-kidsdemo-list">
                      {demoQuizReady && <p className="landing-kidsdemo-ready">{t('landing.kidsDemo.demoQuizReady', language)}</p>}
                      {demoQuizQuestions.map((question) => (
                        <article className="landing-kidsdemo-question" key={question.question}>
                          <strong>{question.question}</strong>
                          <ul>
                            {question.options.map((option) => (
                              <li key={option}>{option}</li>
                            ))}
                          </ul>
                        </article>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </section>

        <section className="landing-section landing-difference landing-section-soft">
          <p className="landing-section-kicker">{t('landing.difference.kicker', language)}</p>
          <h2>{t('landing.difference.headline', language)}</h2>
          <div className="landing-table-wrap">
            <table>
              <tbody>
                {differenceRows.map((row, index) => (
                  <tr key={`${row[0]}-${index}`}>
                    <th>{row[0]}</th>
                    <td>{row[1]}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        <section id="privacy" className="landing-section landing-privacy">
          <p className="landing-section-kicker">{t('landing.privacy.kicker', language)}</p>
          <h2>{t('landing.privacy.headline', language)}</h2>
          <ul>
            {privacyPoints.map((point) => (
              <li key={point}>{point}</li>
            ))}
          </ul>
          <div className="landing-firewall-callout">
            <p className="landing-privacy-firewall">{t('landing.privacy.firewall', language)}</p>
            <p>{t('landing.privacy.providersCannotSee', language)}</p>
          </div>
        </section>

        <section className="landing-section landing-insights landing-section-soft">
          <p className="landing-section-kicker">{t('landing.insights.kicker', language)}</p>
          <h2>{t('landing.insights.headline', language)}</h2>
          <div className="landing-insight-cards">
            <article className="landing-card">
              <p className="landing-card-label">{t('landing.insights.card1Label', language)}</p>
              <p>{t('landing.insights.card1Body', language)}</p>
            </article>
            <article className="landing-card">
              <p className="landing-card-label">{t('landing.insights.card2Label', language)}</p>
              <p>{t('landing.insights.card2Body', language)}</p>
            </article>
          </div>
        </section>

        <section id="trust" className="landing-section landing-trust">
          <p className="landing-section-kicker">{t('landing.trust.kicker', language)}</p>
          <h2>{t('landing.trust.headline', language)}</h2>
          <ul>
            {trustPoints.map((point) => (
              <li key={point}>{point}</li>
            ))}
          </ul>
        </section>

        <section className="landing-section landing-pricing landing-section-soft">
          <p className="landing-section-kicker">{t('landing.pricing.kicker', language)}</p>
          <h2>{t('landing.pricing.headline', language)}</h2>
          <div className="landing-pricing-grid">
            <article className="landing-card">
              <h3>{t('landing.pricing.freeTitle', language)}</h3>
              <p>{t('landing.pricing.freeBody', language)}</p>
            </article>
            <article className="landing-card landing-card-highlight">
              <h3>{t('landing.pricing.familyTitle', language)}</h3>
              <p>{t('landing.pricing.familyBody', language)}</p>
            </article>
            <article className="landing-card">
              <h3>{t('landing.pricing.premiumTitle', language)}</h3>
              <p>{t('landing.pricing.premiumBody', language)}</p>
            </article>
          </div>
        </section>

        <section className="landing-section landing-final-cta">
          <img src={ruleeLogoPrimary} alt="Rulee AI" className="landing-inline-logo landing-inline-logo-center" />
          <h2>{t('landing.finalCta.headline', language)}</h2>
          <button className="btn landing-primary-cta" onClick={onStartFamily}>
            {t('landing.finalCta.button', language)}
          </button>
        </section>
      </main>

      <footer className="landing-footer">
        <a href="#privacy">{t('landing.footer.privacyPolicy', language)}</a>
        <a href="#trust">{t('landing.footer.safetyInfo', language)}</a>
        <a href="#how-it-works">{t('landing.footer.parentFaq', language)}</a>
        <a href="mailto:hello@rulee.ai">{t('landing.footer.contact', language)}</a>
        <a href="/terms">{t('landing.footer.terms', language)}</a>
      </footer>
    </div>
  );
};

export default LandingPage;
